import { useCallback, useEffect, useState } from "react";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { Flex, Icon, Icons, Spin, Switch, useToast } from "@ir/ui";
import { Permissions } from "@src/domain/permissions/Permissions";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import { BrandRepository } from "@src/infrastructure/repositories/brand";
import { UpdateBrandPurchaserStatusDTO } from "@src/domain/dto/brands";
import { UpdateBrandPurchaserStatusUseCase } from "@src/application/useCases/brand";
import type { BrandPurchaser } from "@src/domain/entities/brand";

export interface BrandPurchaserStatusUpdateProps {
  brandId: number;
  purchaserId: number;
  brandPurchaserIsActive: boolean;
  rowPurchaser?: BrandPurchaser;
  onPurchaserStatusUpdated?: (purchaser: BrandPurchaser) => void;
}

export const BrandPurchaserStatusUpdate = ({
  brandId,
  purchaserId,
  brandPurchaserIsActive,
  rowPurchaser,
  onPurchaserStatusUpdated,
}: BrandPurchaserStatusUpdateProps) => {
  const [pendingActive, setPendingActive] = useState(brandPurchaserIsActive);
  const [isUpdating, setIsUpdating] = useState(false);
  const { error: errorToast, success: successToast } = useToast();

  useEffect(() => {
    setPendingActive(brandPurchaserIsActive);
  }, [brandPurchaserIsActive]);

  const handleSwitchChange = useCallback(
    async (mode: boolean) => {
      setPendingActive(mode);
      setIsUpdating(true);
      try {
        const payload = { brandId, purchaserId, isActive: mode };
        const validated = UpdateBrandPurchaserStatusDTO.validate(payload);
        if (!validated.success) {
          console.error(
            "UpdateBrandPurchaserStatus validation failed",
            validated.error,
          );
          throw new Error("UpdateBrandPurchaserStatus validation failed");
        }

        const dto = new UpdateBrandPurchaserStatusDTO(validated.data);
        const updatedPurchaser = await new UpdateBrandPurchaserStatusUseCase(
          new BrandRepository(GraphQlAdapter.getInstance().getClient()),
        ).execute(dto);

        onPurchaserStatusUpdated?.(updatedPurchaser);
        successToast({
          content: "Purchaser association updated successfully.",
          icon: <Icon name={Icons.CheckCircle} />,
        });
      } catch (error) {
        console.error("Failed to update brand purchaser status", error);
        setPendingActive(brandPurchaserIsActive);
        if (rowPurchaser) {
          onPurchaserStatusUpdated?.(rowPurchaser);
        }
        errorToast({
          content:
            "Failed to update purchaser association status. Please try again.",
          icon: <Icon name={Icons.AlertCircle} />,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [
      brandId,
      purchaserId,
      brandPurchaserIsActive,
      onPurchaserStatusUpdated,
      successToast,
      errorToast,
      rowPurchaser,
    ],
  );

  return (
    <HasAnyPermission
      requiredPermissions={[
        Permissions.BRANDS_EDIT,
        Permissions.PURCHASERS_EDIT,
      ]}
    >
      <Flex align="center" gap={0.5}>
        {isUpdating && <Spin size="small" spinning />}
        <Switch
          size="small"
          isSelected={pendingActive}
          onChange={handleSwitchChange}
          isDisabled={isUpdating}
          aria-label="Brand purchaser status update"
          role="switch"
          htmlTitle="Update purchaser status"
        />
      </Flex>
    </HasAnyPermission>
  );
};
